import {
  LeftOutlined,
  DownOutlined,
  FileProtectOutlined,
  BankOutlined,
  GlobalOutlined,
  MessageOutlined,
  TruckOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Row,
  Spin,
  Tabs,
  Dropdown,
  Divider,
  notification,
  Alert,
} from "antd";
import {
  CfdiComplementModel,
  ClarificationComplementModel,
  ComplementModel,
  createComplement,
  getComplementDetails,
  InternationalComplementModel,
  StorageComplementModel,
  TransportComplementModel,
} from "api/generated";
import { useClient } from "hooks/useClient";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { validate as isValidUUID } from "uuid";
import { CfdiComplementForm } from "./subComplement/CFDIComplementForm";
import { ClarificationComplementForm } from "./subComplement/ClarificationComplementForm";
import { InternationalComplementForm } from "./subComplement/InternationalComplementForm";
import { StorageComplementForm } from "./subComplement/StorageComplementForm";
import { TransportComplementForm } from "./subComplement/TransportComplementForm";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import dayjs from "dayjs";
import { FormItem } from "common/components/form/FormItem";
import { handleAxiosError } from "common/helpers/generalUtils";
import DistributionPointSelected from "context/MyDistributionPoint";

enum COMPLEMENT_CHILD {
  NACIONAL = "NACIONAL",
  STORAGE = "STORAGE",
  TRANSPORT = "TRANSPORT",
  INTERNATIONAL = "INTERNATIONAL",
  CLARIFICATION = "CLARIFICATION",
}

interface ComplementLabelConfig {
  icon: ReactNode;
  label: string;
  key: COMPLEMENT_CHILD;
}

export const COMPLEMENT_LABELS: Record<
  COMPLEMENT_CHILD,
  ComplementLabelConfig
> = {
  [COMPLEMENT_CHILD.NACIONAL]: {
    icon: <FileProtectOutlined />,
    label: "CFDI",
    key: COMPLEMENT_CHILD.NACIONAL,
  },
  [COMPLEMENT_CHILD.STORAGE]: {
    icon: <BankOutlined />,
    label: "Almacenamiento",
    key: COMPLEMENT_CHILD.STORAGE,
  },
  [COMPLEMENT_CHILD.TRANSPORT]: {
    icon: <TruckOutlined />,
    label: "Transporte",
    key: COMPLEMENT_CHILD.TRANSPORT,
  },
  [COMPLEMENT_CHILD.INTERNATIONAL]: {
    icon: <GlobalOutlined />,
    label: "Internacional",
    key: COMPLEMENT_CHILD.INTERNATIONAL,
  },
  [COMPLEMENT_CHILD.CLARIFICATION]: {
    icon: <MessageOutlined />,
    label: "Aclaración",
    key: COMPLEMENT_CHILD.CLARIFICATION,
  },
};

export enum COMPLEMENT_PARENT {
  RECEPCION = "RECEPCION",
  VENTA = "VENTA",
  TRASPASO = "TRASPASO",
  COMPRA = "COMPRA",
}

interface RouteParams {
  parentType: COMPLEMENT_PARENT;
  parentId: string;
  distributionPointId: string;
}

function ComplementsScreen({
  match,
  history,
}: RouteComponentProps<RouteParams>) {
  const { parentType, parentId, distributionPointId } = match.params;
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(COMPLEMENT_CHILD.NACIONAL);
  const [complementData] = Form.useForm<ComplementModel>();
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});

  const [activeComplements, setActiveComplements] = useState<
    Set<COMPLEMENT_CHILD>
  >(new Set());

  const nacionalData: CfdiComplementModel[] =
    Form.useWatch("nacional", { form: complementData, preserve: true }) || [];

  const clarificationtData: ClarificationComplementModel | undefined =
    Form.useWatch("clarification", { form: complementData, preserve: true });

  const storageData: StorageComplementModel | undefined = Form.useWatch(
    "storage",
    { form: complementData, preserve: true }
  );
  const transportData: TransportComplementModel | undefined = Form.useWatch(
    "transport",
    { form: complementData, preserve: true }
  );
  const internationalData: InternationalComplementModel | undefined =
    Form.useWatch("international", { form: complementData, preserve: true });

  useEffect(() => {
    if (!distributionPoint || !distributionPointId) return;
    if (distributionPointId !== distributionPoint?.id) {
      history.goBack();
    }
  }, [distributionPoint?.id]);

  // Cargar datos del complemento
  useEffect(
    function loadComplementData() {
      if (isValidUUID(parentId)) {
        setIsLoading(true);
        getComplementDetails({
          client,
          path: {
            parentId,
            parentType: parentType,
          },
        })
          .then(({ data }) => {
            complementData.setFieldsValue(data || {});
            setIsEditable(data?.editable ?? false);

            // Actualizar activeComplements basado en los datos cargados
            const newActiveComplements = new Set<COMPLEMENT_CHILD>();
            if (data?.nacional) {
              newActiveComplements.add(COMPLEMENT_CHILD.NACIONAL);
            }
            if (data?.storage) {
              newActiveComplements.add(COMPLEMENT_CHILD.STORAGE);
            }
            if (data?.transport) {
              newActiveComplements.add(COMPLEMENT_CHILD.TRANSPORT);
            }
            if (data?.international) {
              newActiveComplements.add(COMPLEMENT_CHILD.INTERNATIONAL);
            }
            if (data?.clarification) {
              newActiveComplements.add(COMPLEMENT_CHILD.CLARIFICATION);
            }

            setActiveComplements(newActiveComplements);
            if (newActiveComplements.size > 0) {
              setActiveTab(Array.from(newActiveComplements)[0]);
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [parentId, parentType, client, complementData]
  );

  function handleErros(error: {
    errorFields?: Array<{ name: string[]; errors: string[] }>;
  }) {
    const formMapNames: Record<string, COMPLEMENT_CHILD> = {
      nacional: COMPLEMENT_CHILD.NACIONAL,
      storage: COMPLEMENT_CHILD.STORAGE,
      transport: COMPLEMENT_CHILD.TRANSPORT,
      international: COMPLEMENT_CHILD.INTERNATIONAL,
      clarification: COMPLEMENT_CHILD.CLARIFICATION,
    };
    const newErrors: Record<string, string[]> = {};
    if (error.errorFields) {
      error.errorFields.forEach(
        (fieldError: { name: string[]; errors: string[] }) => {
          // Los nombres de campo vienen como array: ['nacional', 'uuidSAT']
          const fieldPath = fieldError.name;

          // El primer elemento del array es el tipo de complemento (nacional|storage|...)
          const fieldName = fieldPath[0] as string;
          const complementType = formMapNames[fieldName];
          if (complementType) {
            if (!newErrors[complementType]) {
              newErrors[complementType] = [];
            }
            // Agregar el primer error para este complemento
            if (fieldError.errors.length > 0) {
              newErrors[complementType].push(fieldError.errors[0]);
            }
          } else {
            console.error("Tipo de complemento no mapeado:", fieldName);
          }
        }
      );
    }
    setFormErrors(newErrors);

    // Mostrar notificación y cambiar a la primera pestaña con error
    if (Object.keys(newErrors).length > 0) {
      const firstErrorTab = Object.keys(newErrors)[0] as COMPLEMENT_CHILD;
      setActiveTab(firstErrorTab);

      notification.error({
        message: "Errores en el formulario",
        description: `Hay errores en la pestaña "${COMPLEMENT_LABELS[firstErrorTab].label}". Por favor, revisa la información.`,
        duration: 5,
      });
    }
  }

  // Validar que el nodo nacional tenga al menos un item
  function validateNacionalData(): boolean {
    if (
      !nacionalData ||
      !Array.isArray(nacionalData) ||
      nacionalData.length === 0
    ) {
      return false;
    }

    // Validar que al menos un item tenga datos básicos (UUID SAT es requerido)
    return nacionalData.some(
      (item) => item && item.fiscalId && item.fiscalId.trim() !== ""
    );
  }

  // Guardar cambios
  function handleSave() {
    if (!isValidUUID(parentId)) {
      notification.error({
        message: "Error al procesar la información",
        description: `El identificador de tu ${parentType} no tiene un formato valido: ${parentId}`,
      });
      return;
    }

    complementData
      .validateFields()
      .then((values) => {
        if (
          activeComplements.has(COMPLEMENT_CHILD.NACIONAL) &&
          !validateNacionalData()
        ) {
          setActiveTab(COMPLEMENT_CHILD.NACIONAL);
          notification.error({
            message: "Datos incompletos",
            description:
              "El complemento nacional debe tener al menos un CFDI válido con UUID SAT.",
            duration: 5,
          });
          return;
        }

        setFormErrors({});
        setIsLoading(true);

        const nodoNacional: CfdiComplementModel[] = nacionalData.map((item) => {
          return {
            ...item,
            invoiceDate: dayjs(item.invoiceDate).format("YYYY-MM-DD HH:mm:ss"),
          };
        });

        createComplement({
          client,
          body: {
            nacional: nodoNacional.length === 0 ? undefined : nodoNacional,
            storage: storageData,
            transport: transportData,
            international: internationalData,
            clarification: clarificationtData,
            complementId: values.complementId,
          },
          path: {
            parentId,
            parentType: parentType,
          },
        })
          .then(() => {
            history.goBack();
          })
          .catch((err) =>
            handleAxiosError(err, "Error al almacenar el complemento")
          )
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((error) => {
        handleErros(error);
      });
  }

  // Agregar nuevo sub-complemento
  function handleAddSubComplement(type: COMPLEMENT_CHILD) {
    switch (type) {
      case COMPLEMENT_CHILD.NACIONAL:
        complementData.setFieldValue("nacional", []);
        break;
      case COMPLEMENT_CHILD.STORAGE:
        complementData.setFieldValue("storage", {});
        break;
      case COMPLEMENT_CHILD.TRANSPORT:
        complementData.setFieldValue("transport", {});
        break;
      case COMPLEMENT_CHILD.INTERNATIONAL:
        complementData.setFieldValue("international", {});
        break;
      case COMPLEMENT_CHILD.CLARIFICATION:
        complementData.setFieldValue("clarification", {});
        break;
    }
    setActiveComplements((prev) => new Set(prev).add(type));
    setActiveTab(type);
  }

  function getComplementTabLabel(
    type: COMPLEMENT_CHILD,
    hasError: boolean
  ): ReactNode {
    const config = COMPLEMENT_LABELS[type];
    return (
      <span>
        {config.label}
        {hasError && (
          <ExclamationCircleOutlined
            style={{
              color: "#ff4d4f",
              fontSize: "14px",
              marginLeft: 10,
            }}
          />
        )}
      </span>
    );
  }

  function getComplementDropdownLabel(type: COMPLEMENT_CHILD): ReactNode {
    const config = COMPLEMENT_LABELS[type];
    return (
      <span>
        {config.icon} {config.label}
      </span>
    );
  }

  // Obtener tipos de complemento disponibles para agregar
  const availableTypes = useMemo(() => {
    return Object.values(COMPLEMENT_CHILD).filter(
      (type) => !activeComplements.has(type)
    );
  }, [activeComplements]);

  // Items para el dropdown
  const dropdownItems = useMemo(() => {
    return availableTypes.map((type) => ({
      key: type,
      label: getComplementDropdownLabel(type),
      onClick: () => handleAddSubComplement(type),
    }));
  }, [availableTypes]);

  const itemTabs = useMemo(() => {
    const tabs = [];

    if (activeComplements.has(COMPLEMENT_CHILD.NACIONAL)) {
      const hasError = !!formErrors[COMPLEMENT_CHILD.NACIONAL];
      tabs.push({
        key: COMPLEMENT_CHILD.NACIONAL,
        label: getComplementTabLabel(COMPLEMENT_CHILD.NACIONAL, hasError),
        children: (
          <CfdiComplementForm
            parentType={parentType}
            nacionalData={nacionalData}
            setNationalNode={(cfdiNode) => {
              complementData.setFieldValue("nacional", cfdiNode);
            }}
          />
        ),
        icon: COMPLEMENT_LABELS[COMPLEMENT_CHILD.NACIONAL].icon,
        closable: true,
      });
    }
    if (activeComplements.has(COMPLEMENT_CHILD.STORAGE)) {
      const hasError = !!formErrors[COMPLEMENT_CHILD.STORAGE];
      tabs.push({
        key: COMPLEMENT_CHILD.STORAGE,
        label: getComplementTabLabel(COMPLEMENT_CHILD.STORAGE, hasError),
        children: <StorageComplementForm />,
        icon: COMPLEMENT_LABELS[COMPLEMENT_CHILD.STORAGE].icon,
        closable: true,
      });
    }
    if (activeComplements.has(COMPLEMENT_CHILD.TRANSPORT)) {
      const hasError = !!formErrors[COMPLEMENT_CHILD.TRANSPORT];
      tabs.push({
        key: COMPLEMENT_CHILD.TRANSPORT,
        label: getComplementTabLabel(COMPLEMENT_CHILD.TRANSPORT, hasError),
        children: <TransportComplementForm />,
        icon: COMPLEMENT_LABELS[COMPLEMENT_CHILD.TRANSPORT].icon,
        closable: true,
      });
    }
    if (activeComplements.has(COMPLEMENT_CHILD.INTERNATIONAL)) {
      const hasError = !!formErrors[COMPLEMENT_CHILD.INTERNATIONAL];
      tabs.push({
        key: COMPLEMENT_CHILD.INTERNATIONAL,
        label: getComplementTabLabel(COMPLEMENT_CHILD.INTERNATIONAL, hasError),
        children: <InternationalComplementForm />,
        icon: COMPLEMENT_LABELS[COMPLEMENT_CHILD.INTERNATIONAL].icon,
        closable: true,
      });
    }
    if (activeComplements.has(COMPLEMENT_CHILD.CLARIFICATION)) {
      const hasError = !!formErrors[COMPLEMENT_CHILD.CLARIFICATION];
      tabs.push({
        key: COMPLEMENT_CHILD.CLARIFICATION,
        label: getComplementTabLabel(COMPLEMENT_CHILD.CLARIFICATION, hasError),
        children: <ClarificationComplementForm />,
        icon: COMPLEMENT_LABELS[COMPLEMENT_CHILD.CLARIFICATION].icon,
        closable: true,
      });
    }

    return tabs;
  }, [activeComplements, formErrors, complementData, nacionalData]);

  function handleTabEdit(
    targetKey: string | React.MouseEvent | React.KeyboardEvent,
    action: "add" | "remove"
  ) {
    if (action === "remove" && typeof targetKey === "string") {
      const type = targetKey as COMPLEMENT_CHILD;

      // Remover del conjunto de complementos activos
      setActiveComplements((prev) => {
        const newSet = new Set(prev);
        newSet.delete(type);
        return newSet;
      });

      // Limpiar los datos del formulario para ese complemento
      switch (type) {
        case COMPLEMENT_CHILD.NACIONAL:
          complementData.setFieldValue("nacional", undefined);
          break;
        case COMPLEMENT_CHILD.STORAGE:
          complementData.setFieldValue("storage", undefined);
          break;
        case COMPLEMENT_CHILD.TRANSPORT:
          complementData.setFieldValue("transport", undefined);
          break;
        case COMPLEMENT_CHILD.INTERNATIONAL:
          complementData.setFieldValue("international", undefined);
          break;
        case COMPLEMENT_CHILD.CLARIFICATION:
          complementData.setFieldValue("clarification", undefined);
          break;
      }

      // Si la pestaña activa fue eliminada, cambiar a la primera disponible
      if (targetKey === activeTab && itemTabs.length > 1) {
        const remainingTabs = itemTabs.filter((tab) => tab.key !== targetKey);
        if (remainingTabs.length > 0) {
          setActiveTab(remainingTabs[0].key);
        }
      }
    }
  }

  return (
    <Spin spinning={isLoading}>
      <Form
        layout="vertical"
        form={complementData}
        scrollToFirstError
        disabled={!isEditable}
        style={{
          minHeight: "calc(100vh - 120px)",
          paddingBottom: "80px",
        }}
      >
        <FormItem<ComplementModel> hidden label="id" name={["complementId"]} />
        <Row justify="space-between" align="middle">
          <span style={{ fontSize: "15px", color: "#606676" }}>
            Los campos a continuación corresponden al complemento de la{" "}
            {parentType.toLowerCase()}
          </span>
        </Row>
        {!isEditable && (
          <Row gutter={24}>
            <Divider />
            <ResponsiveCol size="xl">
              <Alert
                message="Complemento no editable"
                description="El complemento ha sido marcado como no editable, por tanto no será posible hacer alguna modificación, ni agregar ningún tipo de sub complemento."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </ResponsiveCol>
          </Row>
        )}
        <Divider />
        {availableTypes.length !== 0 && (
          <Row gutter={24}>
            <ResponsiveCol size="xl">
              <Dropdown
                menu={{ items: dropdownItems }}
                placement="bottomRight"
                disabled={availableTypes.length === 0 || !isEditable}
                open={itemTabs.length === 0 ? true : undefined}
              >
                <Button
                  type="primary"
                  icon={<DownOutlined />}
                  disabled={!isEditable}
                  style={{ float: "right" }}
                >
                  Agregar al Complemento
                </Button>
              </Dropdown>
            </ResponsiveCol>
          </Row>
        )}

        <Tabs
          type="editable-card"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={itemTabs}
          onEdit={handleTabEdit}
          hideAdd
        />
      </Form>

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "5rem",
          right: 0,
          background: "#fff",
          padding: "16px 24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          zIndex: 1000,
        }}
      >
        <Button
          size="large"
          icon={<LeftOutlined />}
          onClick={() => history.goBack()}
        >
          Regresar
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          size="large"
          disabled={itemTabs.length === 0}
        >
          Guardar
        </Button>
      </div>
    </Spin>
  );
}

export default withRouter(ComplementsScreen);
